import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

import { BACKEND_URL, API } from '../utils/apiConfig';

const MasterDataContext = createContext(null);

export const useMasterData = () => {
  const context = useContext(MasterDataContext);
  if (!context) {
    throw new Error('useMasterData must be used within MasterDataProvider');
  }
  return context;
};

export const MasterDataProvider = ({ children }) => {
  const [venues, setVenues] = useState([]);
  const [allGames, setAllGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);

  const fetchMasterData = useCallback(async (forceRefresh = false) => {
    // Skip if data was fetched recently (within 5 minutes) and not forcing refresh
    if (!forceRefresh && lastFetch && Date.now() - lastFetch < 5 * 60 * 1000 && venues.length > 0) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Get auth token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        // User not logged in - will load data after login
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API}/masters/venues-with-courts`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setVenues(response.data.venues || []);
      setAllGames(response.data.all_games || []);
      setLastFetch(Date.now());
    } catch (err) {
      console.error('Failed to fetch master data:', err);
      setError('Failed to load venue and court data');
      // Set fallback data if API fails
      setVenues([]);
      setAllGames([]);
    } finally {
      setLoading(false);
    }
  }, [lastFetch, venues.length]);

  // Initial fetch and re-fetch when token becomes available
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && venues.length === 0) {
      fetchMasterData(true);
    } else if (!token) {
      setLoading(false);
    }
  }, []);

  // Listen for storage changes (login/logout in other tabs) and token changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        if (e.newValue) {
          // Token was added - fetch data
          fetchMasterData(true);
        } else {
          // Token was removed - clear data
          setVenues([]);
          setAllGames([]);
          setLastFetch(null);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [fetchMasterData]);

  // Helper functions for accessing data
  const getVenueNames = useCallback(() => {
    return venues.filter(v => v.is_active).map(v => v.name);
  }, [venues]);

  const getGamesForVenue = useCallback((venueName) => {
    const venue = venues.find(v => v.name === venueName);
    return venue?.games || [];
  }, [venues]);

  const getCourtsForVenueGame = useCallback((venueName, gameName) => {
    const venue = venues.find(v => v.name === venueName);
    if (!venue) return [];
    
    // Filter courts that have this game
    return venue.courts
      .filter(c => c.is_active && c.games.includes(gameName))
      .map(c => c.name);
  }, [venues]);

  const getVenueById = useCallback((venueId) => {
    return venues.find(v => v.id === venueId);
  }, [venues]);

  const getVenueByName = useCallback((venueName) => {
    return venues.find(v => v.name === venueName);
  }, [venues]);

  const getAllCourtsForVenue = useCallback((venueName) => {
    const venue = venues.find(v => v.name === venueName);
    return venue?.courts?.filter(c => c.is_active).map(c => c.name) || [];
  }, [venues]);

  const value = {
    // Data
    venues,
    allGames,
    loading,
    error,
    
    // Actions
    refreshData: () => fetchMasterData(true),
    
    // Helper functions (for easy use in components)
    getVenueNames,
    getGamesForVenue,
    getCourtsForVenueGame,
    getVenueById,
    getVenueByName,
    getAllCourtsForVenue,
  };

  return (
    <MasterDataContext.Provider value={value}>
      {children}
    </MasterDataContext.Provider>
  );
};

export default MasterDataContext;
